import { useRef, useState } from "react";
import { useNotify } from "ra-core";
import { Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { interpretDictation, type DictationResult } from "./logActivity";

// Web Speech API (gratis, del navegador). Chrome la expone con prefijo webkit.
type Recognition = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start: () => void;
  stop: () => void;
  onresult: (e: {
    results: ArrayLike<ArrayLike<{ transcript: string }>>;
  }) => void;
  onerror: (e: { error: string }) => void;
  onend: () => void;
};
const SpeechRecognition = (
  typeof window !== "undefined"
    ? ((window as any).SpeechRecognition ??
      (window as any).webkitSpeechRecognition)
    : undefined
) as (new () => Recognition) | undefined;

/** Botón "Dictar": se oculta si el navegador no soporta dictado. */
export const DictateButton = ({
  onResult,
  className,
}: {
  onResult: (r: DictationResult) => void;
  className?: string;
}) => {
  const [listening, setListening] = useState(false);
  const [thinking, setThinking] = useState(false);
  const rec = useRef<Recognition | null>(null);
  const notify = useNotify();
  if (!SpeechRecognition) return null;

  const start = () => {
    const r = new SpeechRecognition();
    r.lang = "es-MX";
    r.interimResults = false;
    r.continuous = false;
    r.onresult = async (e) => {
      const text = Array.from(e.results)
        .map((res) => res[0].transcript)
        .join(" ")
        .trim();
      if (!text) return;
      setThinking(true);
      const res = await interpretDictation(text);
      setThinking(false);
      onResult(res);
      notify(
        res.ai
          ? "La IA organizó lo que dictaste: revisa resultado, nota y siguiente paso antes de guardar"
          : "Dictado listo: revisa la nota antes de guardar",
        { type: "info" },
      );
    };
    r.onerror = (e) => {
      if (e.error === "not-allowed")
        notify("Permite el micrófono para dictar", { type: "warning" });
      else if (e.error !== "no-speech" && e.error !== "aborted")
        notify("No se pudo dictar; escribe la nota", { type: "warning" });
    };
    r.onend = () => setListening(false);
    rec.current = r;
    setListening(true);
    r.start();
  };

  return (
    <Button
      type="button"
      variant="outline"
      className={className}
      disabled={thinking}
      onClick={() => (listening ? rec.current?.stop() : start())}
    >
      {listening ? <MicOff /> : <Mic />}
      {thinking
        ? "Organizando con IA…"
        : listening
          ? "Escuchando… toca para terminar"
          : "Dictar"}
    </Button>
  );
};
